// eslint-disable-next-line import/no-cycle
import enumsService from '../../services/enumService';

export default {
  namespaced: true,

  state: {
    enumTypes: [],
    currentEnum: null,
    loading: false,
    error: null,
  },

  mutations: {
    SET_ENUM_TYPES(state, enums) {
      // Ensure that enumTypes is an array
      state.enumTypes = Array.isArray(enums) ? enums : [enums];
    },
    SET_CURRENT_ENUM(state, enumType) {
      state.currentEnum = enumType;
    },
    ADD_ENUM_TYPE(state, enumType) {
      // Add the new enum to the array (preserving the array structure)
      state.enumTypes = [...state.enumTypes, enumType];
    },
    UPDATE_ENUM_TYPE(state, updatedEnum) {
      const index = state.enumTypes.findIndex((e) => e._id === updatedEnum._id);
      if (index !== -1) {
        state.enumTypes.splice(index, 1, updatedEnum);
      }
      if (state.currentEnum && state.currentEnum._id === updatedEnum._id) {
        state.currentEnum = updatedEnum;
      }
    },
    REMOVE_ENUM_TYPE(state, enumName) {
      state.enumTypes = state.enumTypes.filter((e) => e.name !== enumName);
      if (state.currentEnum && state.currentEnum.name === enumName) {
        state.currentEnum = null;
      }
    },
    ADD_ENUM_VALUE(state, { enumId, value }) {
      const enumType = state.enumTypes.find((e) => e._id === enumId);
      if (enumType) {
        enumType.values.push(value);
      }
      if (state.currentEnum && state.currentEnum._id === enumId) {
        state.currentEnum.values.push(value);
      }
    },
    UPDATE_ENUM_VALUE(state, { enumId, value }) {
      const enumType = state.enumTypes.find((e) => e._id === enumId);
      if (enumType) {
        const index = enumType.values.findIndex((v) => v.value === value.value);
        if (index !== -1) {
          enumType.values.splice(index, 1, value);
        }
      }
      if (state.currentEnum && state.currentEnum._id === enumId) {
        const index = state.currentEnum.values.findIndex((v) => v.value === value.value);
        if (index !== -1) {
          state.currentEnum.values.splice(index, 1, value);
        }
      }
    },
    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
  },

  actions: {
    clearError({ commit }) {
      commit('SET_ERROR', null);
    },

    async fetchEnumTypes({ commit, dispatch }) {
      commit('SET_LOADING', true);
      try {
        const response = await enumsService.getAllEnumTypes();
        commit('SET_ENUM_TYPES', response.data);
        commit('SET_ERROR', null);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch enum types');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to fetch enum types',
            severity: 'error',
          },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchEnumType({ commit, dispatch }, enumId) {
      commit('SET_LOADING', true);
      try {
        const response = await enumsService.getEnumType(enumId);
        commit('SET_CURRENT_ENUM', response.data);
        commit('SET_ERROR', null);
      } catch (error) {
        commit(
          'SET_ERROR',
          error.response?.data?.message || `Failed to fetch enum type: ${enumId}`,
        );
        dispatch(
          'notifications/showToast',
          {
            detail: `Failed to fetch enum type: ${enumId}`,
            severity: 'error',
          },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createEnumType({ commit, dispatch }, enumData) {
      commit('SET_LOADING', true);
      try {
        const response = await enumsService.createEnumType(enumData);
        commit('ADD_ENUM_TYPE', response.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Enum type created successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to create enum type');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to create enum type',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async addEnumValue({ commit, dispatch }, { enumName, valueData }) {
      commit('SET_LOADING', true);
      try {
        const response = await enumsService.addEnumValue(enumName, valueData);
        commit('UPDATE_ENUM_TYPE', response.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Enum value added successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to add enum value');
        dispatch(
          'notifications/showToast',
          {
            title: 'Enum Creation Error',
            detail: 'Failed to add enum value',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deactivateEnumValue({ commit, dispatch }, { enumName, value }) {
      commit('SET_LOADING', true);
      try {
        await enumsService.deactivateEnumValue(enumName, value);
        // PATCH returns `{ modifiedCount }` only — refetch full enum for store + UI
        const refresh = await enumsService.getEnumType(enumName, false);
        commit('UPDATE_ENUM_TYPE', refresh.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Enum value deactivated successfully',
            severity: 'success',
          },
          { root: true },
        );
        return refresh.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to deactivate enum value');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to deactivate enum value',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async activateEnumValue({ commit, dispatch }, { enumName, value }) {
      commit('SET_LOADING', true);
      try {
        await enumsService.activateEnumValue(enumName, value);
        const refresh = await enumsService.getEnumType(enumName, false);
        commit('UPDATE_ENUM_TYPE', refresh.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Enum value activated successfully',
            severity: 'success',
          },
          { root: true },
        );
        return refresh.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to activate enum value');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to activate enum value',
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteEnumValue({ commit, dispatch }, { enumName, value }) {
      commit('SET_LOADING', true);
      try {
        const response = await enumsService.deleteEnumValue(enumName, value);
        commit('UPDATE_ENUM_TYPE', response.data);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Enum value deleted successfully',
            severity: 'success',
          },
          { root: true },
        );
        return response.data;
      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to delete enum value';
        commit('SET_ERROR', msg);
        dispatch(
          'notifications/showToast',
          {
            detail: msg,
            severity: 'error',
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteEnumType({ commit, dispatch }, enumTypeName) {
      commit('SET_LOADING', true);
      try {
        await enumsService.deleteEnumType(enumTypeName);
        commit('REMOVE_ENUM_TYPE', enumTypeName);
        commit('SET_ERROR', null);
        dispatch(
          'notifications/showToast',
          {
            detail: 'Enum type deleted successfully',
            severity: 'success',
          },
          { root: true },
        );
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Failed to delete enum type');
        dispatch(
          'notifications/showToast',
          {
            detail: 'Failed to delete enum type',
            severity: 'error',
          },
          { root: true },
        );
      } finally {
        commit('SET_LOADING', false);
      }
    },
  },

  getters: {
    // eslint-disable-next-line max-len
    getEnumTypeByName: (state) => (name) => state.enumTypes.find((enumType) => enumType.name === name),
    getActiveEnumValues: (state) => (name) => {
      const enumType = state.enumTypes.find((type) => type.name === name);
      return enumType ? enumType.values.filter((v) => v.isActive) : [];
    },
    isLoading: (state) => state.loading,
    error: (state) => state.error,
  },
};
